/**
 * Navigation components.
 * @module components/theme/Navigation/Navigation
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'classnames';
import { flattenToAppURL } from '@plone/volto/helpers';
import OutsideClickHandler from 'react-outside-click-handler';
import { getDropdownMenuNavitems, getItemsByPath } from 'volto-dropdownmenu';
import { Menu, Button, Icon } from 'semantic-ui-react';
import config from '~/config.js';
import DropdownMenu from '@package/components/DropdownMenu/DropdownMenu';
import { Logo } from '@plone/volto/components';

const messages = defineMessages({
  closeMobileMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openMobileMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  dropdownmenu_aria: {
    id: 'Main navigation',
    defaultMessage: 'Main navigation',
  },
});

const Navigation = ({ pathname, type }) => {
  const { settings } = config;
  const dispatch = useDispatch();
  const items = useSelector((state) => state.dropdownMenuNavItems?.result);
  useEffect(() => {
    dispatch(getDropdownMenuNavitems());
  }, [dispatch]);
  const intl = useIntl();
  const { lang } = intl.locale;
  const menu = getItemsByPath(items, '/');
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdownIndex, setOpenDropodownIndex] = useState(-1);
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropodownIndex(-1);
  }, [pathname]);

  const getAnchorTarget = (nodeElement) => {
    if (nodeElement.nodeName === 'A') {
      return nodeElement;
    } else if (nodeElement.parentElement?.nodeName === 'A') {
      return nodeElement.parentElement;
    } else {
      return null;
    }
  };

  useEffect(() => {
    const clickListener = (e) => {
      const targetItem = getAnchorTarget(e.target);
      const dropdownmenuLinks = [
        ...(document?.querySelectorAll(
          '.navigation-dropdownmenu .dropdown-menu-wrapper ul li a, .navigation-dropdownmenu .dropdownmenu-blocks-column a, .dropdownmenu-footer a, .navigation-dropdownmenu .menu > a',
        ) ?? []),
      ];

      if (
        dropdownmenuLinks?.length > 0 &&
        dropdownmenuLinks?.indexOf(targetItem) >= 0
      ) {
        setOpenDropodownIndex(-1);
        setMobileMenuOpen(false); //close mobile menu
      }
    };

    document.body.addEventListener('click', clickListener);

    return () => document.body.removeEventListener('click', clickListener);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const elements = document.querySelectorAll('.menu-container');
    if (isMobileMenuOpen) {
      elements.forEach((element) => {
        element.classList.add('open');
      });
    } else
      elements.forEach((element) => {
        element.classList.remove('open');
      });
  }, [isMobileMenuOpen]);

  const toggleDropdownMenu = (index) => {
    if (openDropdownIndex === index) setOpenDropodownIndex(-1);
    else setOpenDropodownIndex(index);
  };

  const isMenuActive = (item) => {
    const paths = [...(item.navigationRoot ?? [])];
    if (item.showMoreLink?.length > 0) {
      paths.push(item.showMoreLink[0]);
    }

    return paths.reduce(
      (acc, path) =>
        acc ||
        flattenToAppURL(pathname).indexOf(flattenToAppURL(path['@id'])) > -1,
      false,
    );
  };

  const resetMenuSimpleLink = (index) => {
    if (isMobileMenuOpen) toggleMobileMenu();
    if (openDropdownIndex !== index) setOpenDropodownIndex(-1);
  };

  return (
    <nav
      className="navigation navigation-dropdownmenu"
      role="navigation"
      aria-label={intl.formatMessage(messages.dropdownmenu_aria)}
    >
      <OutsideClickHandler
        onOutsideClick={() => {
          setOpenDropodownIndex(-1);
          if (isMobileMenuOpen) {
            setMobileMenuOpen(false);
          }
        }}
      >
        <div className="hamburger-wrapper mobile tablet only">
          <button
            className={cx('hamburger hamburger--collapse', {
              'is-active': isMobileMenuOpen,
            })}
            aria-label={
              isMobileMenuOpen
                ? intl.formatMessage(messages.closeMobileMenu, {
                    type,
                  })
                : intl.formatMessage(messages.openMobileMenu, {
                    type,
                  })
            }
            title={
              isMobileMenuOpen
                ? intl.formatMessage(messages.closeMobileMenu, {
                    type,
                  })
                : intl.formatMessage(messages.openMobileMenu, {
                    type,
                  })
            }
            type="button"
            onClick={toggleMobileMenu}
          >
            <span className="hamburger-box">
              <span className="hamburger-inner" />
            </span>
          </button>
        </div>
        <div
          className={cx('overlay')}
          style={{ display: isMobileMenuOpen ? 'block' : 'none' }}
        >
          <div className="close-button">
            <Icon
              name="close"
              size="large"
              style={{
                fontSize: '4rem',
                fontWeight: '600',
              }}
              onClick={() => setMobileMenuOpen(false)}
            ></Icon>
          </div>
          <div className={cx('menu-container')}>
            <div className="logo">
              <Logo />
            </div>

            <Menu
              stackable
              pointing
              secondary
              className={`dropdownmenu ${
                isMobileMenuOpen
                  ? 'open'
                  : 'computer large screen widescreen only'
              }`}
            >
              {menu?.length > 0
                ? menu
                    ?.filter((item) => item.visible)
                    ?.filter(
                      (item) =>
                        item.mode === 'dropdown' || item.linkUrl?.[0]?.['@id'],
                    )
                    ?.map((item, index) =>
                      item.mode === 'simpleLink' ? (
                        <Menu.Item
                          as="li"
                          key={'simplelink-menu-' + index}
                          className={cx('item', 'dropdownmenu-item', {
                            [item.additionalClasses]:
                              item.additionalClasses?.length > 0,
                          })}
                        >
                          <NavLink
                            as="a"
                            to={flattenToAppURL(item.linkUrl?.[0]?.['@id'])}
                            key={'simplelink-' + index}
                            className="nav-link active"
                            exact={
                              settings?.isMultilingual
                                ? item.linkUrl?.[0]?.['@id'] === `/${lang}`
                                : item.linkUrl?.[0]?.['@id'] === ''
                            }
                            onClick={() => resetMenuSimpleLink(index)}
                          >
                            <span>{item.title}</span>
                          </NavLink>
                        </Menu.Item>
                      ) : (
                        <React.Fragment key={'dropdown-' + index}>
                          <Menu.Item
                            as="li"
                            key={'dropdown-menu-' + index}
                            className={cx('item', 'dropdownmenu-item', {
                              open: openDropdownIndex === index,
                              active: isMenuActive(item) && index !== 0,
                              [item.additionalClasses]:
                                item.additionalClasses?.length > 0,
                            })}
                            onClick={() => toggleDropdownMenu(index)}
                            aria-haspopup={true}
                          >
                            <NavLink
                              as="a"
                              to={'#'}
                              key={'dropdownlink-' + index}
                              activeClassName="active"
                              className={'nav-link'}
                              aria-expanded={
                                openDropdownIndex === index ? true : false
                              }
                            >
                              <span>{item.title}</span>
                              <Icon
                                name="dropdown"
                                size="large"
                                flipped={
                                  openDropdownIndex === index
                                    ? 'vertically'
                                    : null
                                }
                              />
                            </NavLink>
                          </Menu.Item>
                          <DropdownMenu
                            menu={item}
                            open={openDropdownIndex === index}
                            closeMenu={() => setOpenDropodownIndex(-1)}
                          />
                        </React.Fragment>
                      ),
                    )
                : null}
            </Menu>
          </div>
        </div>
      </OutsideClickHandler>
    </nav>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default Navigation;
